import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { CheckCircle2, FileText, LockKeyhole, Mail, Scale, ShieldAlert } from "lucide-react";

export default function TermsOfUse() {
  const sections = [
    {
      title: "Recruiter Responsibility",
      icon: CheckCircle2,
      items: [
        "Use RecruitGlance only for lawful recruitment, screening, and candidate management workflows.",
        "Upload, import, and process only CVs and candidate information that you are authorized to handle.",
        "Review original CVs and job requirements before making hiring, rejection, or interview decisions.",
        "You are responsible for giving candidates any notices, consent requests, or privacy information required by your recruitment process and local law.",
      ],
    },
    {
      title: "AI Output Restrictions",
      icon: ShieldAlert,
      items: [
        "Do not treat AI rankings, scores, summaries, strengths, or gaps as guaranteed facts.",
        "Do not make fully automated hiring decisions based only on RecruitGlance AI output.",
        "Check for missing context, outdated CV details, formatting extraction errors, and possible bias.",
        "Use RecruitGlance rankings to prioritize human review, not as the sole basis for rejection, shortlisting, interviewing, hiring, compensation, or employment eligibility decisions.",
      ],
    },
    {
      title: "EU AI Act And Human Oversight",
      icon: ShieldCheck,
      items: [
        "RecruitGlance is designed as recruiter decision support for candidate screening, ranking, and evidence review. It is not intended to make fully automated hiring, rejection, interview, compensation, or employment eligibility decisions.",
        "RecruitGlance uses large language model AI services including OpenAI GPT to generate candidate summaries, scoring signals, and match explanations.",
        "Recruiters remain responsible for reviewing the original CV, job requirements, score breakdown, AI explanations, and any missing or uncertain evidence before making a hiring decision.",
        "RecruitGlance provides score breakdowns, detected signals, gaps, notes, scan history, and recruiter-controlled decisions to support transparency and human oversight.",
      ],
    },
    {
      title: "Candidate Data",
      icon: FileText,
      items: [
        "Remove incorrect imports, wrong attachments, duplicate records, or candidate data that is no longer needed.",
        "Do not upload unnecessary sensitive personal data unless it is required and lawful for recruitment.",
        "Handle candidate requests for access, correction, or deletion according to applicable privacy rules.",
        "Do not use RecruitGlance to infer or evaluate protected characteristics such as race, ethnicity, religion, political opinion, health, disability, gender, age, sexual orientation, union membership, or similar protected traits.",
      ],
    },
    {
      title: "Fair Hiring And Anti-Discrimination",
      icon: Scale,
      items: [
        "Use job-relevant criteria when creating recruiter requirements, must-have skills, preferred skills, and candidate scoring rules.",
        "Do not configure scoring criteria in a way that intentionally or indirectly discriminates against protected groups.",
        "If an AI result appears inconsistent, unfair, incomplete, or based on irrelevant information, review the original CV and override the result manually.",
        "You remain responsible for complying with applicable employment, equal opportunity, anti-discrimination, and worker-protection laws.",
      ],
    },
    {
      title: "Account And Access",
      icon: LockKeyhole,
      items: [
        "Keep login credentials, API keys, and mailbox connections secure.",
        "Use only your own account and do not bypass plan limits, scan quotas, rate limits, or feature restrictions.",
        "Disconnect Gmail import or API access when it is no longer needed.",
      ],
    },
    {
      title: "Mailbox Import Consent",
      icon: Mail,
      items: [
        "Email import is optional and requires the recruiter to approve mailbox access through Google or Microsoft before RecruitGlance can search messages.",
        "Mailbox import is used only for the selected job workflow, recent Inbox search window, and PDF CV attachment discovery.",
        "RecruitGlance stages imported email candidates first. The recruiter must review and approve candidates before they are saved or scanned.",
        "The recruiter can reject staged candidates, delete incorrect imports, and revoke Google or Microsoft mailbox access when the connection is no longer needed.",
        "The recruiter is responsible for ensuring they have a lawful basis to access, import, review, and process candidate CVs received by email.",
      ],
    },
    {
      title: "Candidate Email Sending",
      icon: Mail,
      items: [
        "Candidate decision email sending is available only for Pro and Enterprise users.",
        "Before sending candidate emails, the recruiter must connect Gmail or Outlook with sending permission, confirm mailbox sending consent, and keep the email template wording appropriate.",
        "RecruitGlance sends shortlisted or rejection emails through the recruiter's connected Gmail or Outlook mailbox, so candidates see the recruiter mailbox and replies return to the recruiter.",
        "The recruiter remains responsible for the wording, timing, recipients, and legal basis of any candidate communication sent through the platform.",
      ],
    },
    {
      title: "Service Terms",
      icon: Scale,
      items: [
        "Subscriptions, scan quotas, and feature access may differ by plan and billing period.",
        "Subscriptions can be cancelled at any time. No refunds are issued for partial billing periods.",
        "RecruitGlance may block abusive usage, unsafe uploads, or activity that risks candidate privacy.",
        "Pilot accounts are temporary evaluation accounts. They may include limited scan quotas, limited duration, and access to paid features for validation only.",
        "Pilot accounts cannot be converted into paid subscriptions. Paid access requires a separate customer account and subscription.",
        "When a pilot account expires, access is disabled automatically and pilot workspace data is scheduled for deletion one day after the 2-day pilot period ends.",
      ],
    },
    {
      title: "Governing Law And Contact",
      icon: Scale,
      items: [
        "These terms are governed by German law.",
        "Company/contact name: RecruitGlance.",
        "For support, billing, legal, or account questions, contact RecruitGlance at recruitglance@gmail.com.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Navbar />

      <main className="px-6 py-12">
        <div className="mx-auto max-w-5xl">
          <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              RecruitGlance Terms of Use
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">
              Use RecruitGlance as recruiter decision support.
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-7 text-slate-600">
              These terms explain the main responsibilities for using RecruitGlance,
              especially when processing candidate personal data and reviewing
              AI-assisted screening output.
            </p>
            <p className="mt-4 text-sm text-slate-500">
              Last updated: August 2, 2026
            </p>
          </div>

          <div className="mt-8 grid gap-5">
            {sections.map(({ title, icon: Icon, items }) => (
              <section key={title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-950 text-white">
                    <Icon size={20} />
                  </span>
                  <h2 className="text-xl font-semibold text-slate-950">{title}</h2>
                </div>
                <ul className="mt-5 space-y-3">
                  {items.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-6 text-slate-600">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/" className="inline-flex min-h-[42px] items-center justify-center rounded-lg bg-emerald-800 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900">
              Back to Home
            </Link>
            <Link to="/privacy" className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-800 transition hover:bg-slate-100">
              Privacy Policy
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
